import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  Container,
  Typography,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@mui/icons-material';

// --- Helper Functions & Data ---

// Mock Data
const createData = (id, name, status, date) => {
  return { id, name, status, date: new Date(date) };
};

const initialRows = [
  createData(1, 'Call with Alice', 'Completed', '2026-03-09'),
  createData(2, 'Follow-up with Bob', 'Pending', '2026-03-10'),
  createData(3, 'New lead: Charlie', 'In Progress', '2026-03-09'),
  createData(4, 'Support call', 'Completed', '2026-03-08'),
  createData(5, 'Internal meeting', 'Pending', '2026-03-11'),
  createData(6, 'Review proposal', 'In Progress', '2026-03-12'),
];

const headCells = [
  { id: 'id', numeric: true, disablePadding: false, label: 'ID' },
  { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
  { id: 'date', numeric: true, disablePadding: false, label: 'Date' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}


// --- Main Component ---

function DoCallsComponent() {
  const navigate = useNavigate();
  const [rows] = useState(initialRows);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filters, setFilters] = useState({
    id: '',
    name: '',
    status: '',
    date: '',
  });

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (event, columnId) => {
    setFilters({
      ...filters,
      [columnId]: event.target.value,
    });
  };

  const visibleRows = useMemo(() => {
    let filteredRows = rows.filter(row => {
      return Object.keys(filters).every(key => {
        const filterValue = filters[key].toLowerCase();
        if (!filterValue) return true;
        
        const rowValue = row[key];
        if (key === 'date') {
          return rowValue.toLocaleDateString().toLowerCase().includes(filterValue);
        }
        return String(rowValue).toLowerCase().includes(filterValue);
      });
    });

    return stableSort(filteredRows, getComparator(order, orderBy));
  }, [rows, order, orderBy, filters]);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{
        p: 2,
        mb: 4,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center'
      }}>
        <IconButton onClick={() => navigate('/')} sx={{ color: 'white', mr: 2 }}>
          <Icons.ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', m: 0 }}>
          _docalls Management
        </Typography>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: 'rgba(255, 255, 255, 0.7) !important',
                        },
                      }}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={`${headCell.id}-filter`}>
                    <TextField
                      variant="standard"
                      size="small"
                      fullWidth
                      placeholder={`Filter ${headCell.label}`}
                      value={filters[headCell.id]}
                      onChange={(e) => handleFilterChange(e, headCell.id)}
                      sx={{
                        '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.42)' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'primary.main' },
                         input: { color: 'white' } 
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.date.toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default DoCallsComponent;
